import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import ShopService from 'services/shop';
import ShopStaffService from 'services/shopStaff';
import ShopDayOffService from 'services/shopDayOff';
import MerchantService from 'services/merchant';
import { get, isEmpty } from 'utils/lodash';
import dayjs, { toUTCString } from 'utils/dayjs';
import { Shop, ShopStaff, ShopDayOff } from './types';

interface MonthlyRevenue {
  month: number;
  year: number;
  totalRevenue: number;
  totalBookings: number;
}

interface ShopState {
  shop: Shop | null;
  shopStaff: ShopStaff[];
  shopDayOffs: ShopDayOff[];
  monthlyRevenue: MonthlyRevenue | null;
  isLoading: boolean;
  isLoadingStaff: boolean;
  isLoadingDayOffs: boolean;
  error: string | null;
  isSSR: boolean;

  // Actions
  loadShopInfo: () => Promise<void>;
  loadShopStaff: (userId?: string) => Promise<any>;
  loadShopDayOffs: (date?: any) => Promise<void>;
  loadMonthlyRevenue: (month: number, year: number) => Promise<void>;
  updateShopSettings: (data: Partial<Shop>) => Promise<void>;
  addShopDayOff: (date: any, reason?: string) => Promise<void>;
  removeShopDayOff: (id: string) => Promise<void>;
  updateShopDayOffInCache: (dayOffData: any) => void;
  setShop: (shop: Shop | null) => void;
  onChangeShop: (shop: Shop) => void;
  autoSelectShop: (shopStaffList: ShopStaff[]) => void;
  clearError: () => void;
  setSSR: (isSSR: boolean) => void;
}

export const useShopStore = create<ShopState>()(
  devtools(
    persist(
      (set, getState) => ({
        shop: null,
        shopStaff: [],
        shopDayOffs: [],
        monthlyRevenue: null,
        isLoading: false,
        isLoadingStaff: false,
        isLoadingDayOffs: false,
        error: null,
        isSSR: true,

        loadShopInfo: async () => {
          set({ isLoading: true, error: null });
          try {
            // TODO: Implement when getShopInfo is available
            // const response = await ShopService.getShopInfo();
            // const shop = get(response, 'data');
            const shop = null; // Mock for now

            if (shop) {
              set({
                shop,
                isLoading: false,
              });
            }
          } catch (error: any) {
            set({
              error: error.message || 'Failed to load shop info',
              isLoading: false,
            });
            console.error('Error loading shop info:', error);
          }
        },

        loadShopStaff: async (userId) => {
          set({ isLoadingStaff: true, error: null });
          try {
            const whereClause = userId
              ? { where: { user: { equals: userId } } }
              : {};

            const response = await ShopStaffService.find({
              ...whereClause,
              page: 1,
              limit: 100,
            });

            const staff = get(response, 'data', []);

            // Auto-select shop when staff is loaded
            if (!isEmpty(staff)) {
              getState().autoSelectShop(staff);
            }

            set({
              shopStaff: staff,
              isLoadingStaff: false,
            });

            return staff;
          } catch (error: any) {
            set({
              error: error.message || 'Failed to load shop staff',
              isLoadingStaff: false,
            });
            console.error('Error loading shop staff:', error);
            return [];
          }
        },

        loadShopDayOffs: async (date?) => {
          const shopId = getState().shop?.id;
          if (!shopId) {
            console.warn('No shop selected');
            return;
          }

          console.log('=== loadShopDayOffs Debug ===');
          console.log('Input date:', date);
          console.log('Shop ID:', shopId);

          set({ isLoadingDayOffs: true, error: null });
          try {
            const selectedDate = date || dayjs();
            // Get start of day in local time
            const localStartOfDay = dayjs(selectedDate).startOf('day');
            // Convert to UTC - this will give us the UTC time when it's midnight in local timezone
            const dateToQuery = localStartOfDay.toISOString();

            console.log('Selected date (local):', selectedDate.format ? selectedDate.format('YYYY-MM-DD HH:mm:ss Z') : selectedDate);
            console.log('Local start of day:', localStartOfDay.format('YYYY-MM-DD HH:mm:ss Z'));
            console.log('Date to query (UTC):', dateToQuery);
            console.log('Timezone offset:', dayjs().utcOffset());

            const response = await ShopDayOffService.find({
              where: {
                shop: { equals: shopId },
                date: {
                  equals: dateToQuery,
                },
              },
              page: 1,
              limit: 100,
              fetchPolicy: 'cache-first',
            });

            console.log('Response:', response);

            const dayOffs = (response?.data || []) as ShopDayOff[];
            console.log('Day offs found:', dayOffs);

            set({
              shopDayOffs: dayOffs,
              isLoadingDayOffs: false,
            });
          } catch (error: any) {
            set({
              error: error.message || 'Failed to load shop day offs',
              isLoadingDayOffs: false,
            });
            console.error('Error loading shop day offs:', error);
          }
        },

        loadMonthlyRevenue: async (month, year) => {
          set({ isLoading: true, error: null });
          try {
            // TODO: Enable when API is available
            // const response = await MerchantService.calculateMonthlyRevenue({
            //   input: { month, year },
            // });

            // Mock data for now
            const mockRevenue: MonthlyRevenue = {
              month,
              year,
              totalRevenue: 0,
              totalBookings: 0,
            };

            set({
              monthlyRevenue: mockRevenue,
              isLoading: false,
            });
          } catch (error: any) {
            set({
              error: error.message || 'Failed to load monthly revenue',
              isLoading: false,
            });
            console.error('Error loading monthly revenue:', error);
          }
        },

        updateShopSettings: async (data) => {
          set({ isLoading: true, error: null });
          try {
            const shopId = getState().shop?.id;
            if (!shopId) throw new Error('Shop ID not found');

            // TODO: Implement when updateShop is available
            // const response = await ShopService.updateShop({
            //   id: shopId,
            //   ...data,
            // });
            // const updatedShop = get(response, 'data');
            const updatedShop = { ...getState().shop, ...data } as Shop;
            if (updatedShop) {
              set({
                shop: updatedShop,
                isLoading: false,
              });
            }
          } catch (error: any) {
            set({
              error: error.message || 'Failed to update shop settings',
              isLoading: false,
            });
            throw error;
          }
        },

        addShopDayOff: async (date, reason?) => {
          const shopId = getState().shop?.id;
          if (!shopId) {
            console.error('Shop ID not found');
            return;
          }

          set({ isLoading: true, error: null });
          try {
            const response = await ShopDayOffService.create({
              data: {
                shop: shopId,
                date: date,
                value: true,
                ...(reason && { reason }),
              },
            });

            // Update cache with the new day off
            if (response?.data) {
              set((state) => ({
                shopDayOffs: [...state.shopDayOffs, response.data],
                isLoading: false,
              }));
            }
          } catch (error: any) {
            set({
              error: error.message || 'Failed to add shop day off',
              isLoading: false,
            });
            console.error('Error adding shop day off:', error);
          }
        },

        removeShopDayOff: async (id) => {
          set({ isLoading: true, error: null });
          try {
            await ShopDayOffService.delete({ id });

            // Update local state
            set((state) => ({
              shopDayOffs: state.shopDayOffs.filter((dayOff) => dayOff.id !== id),
              isLoading: false,
            }));
          } catch (error: any) {
            set({
              error: error.message || 'Failed to remove shop day off',
              isLoading: false,
            });
            throw error;
          }
        },

        updateShopDayOffInCache: (dayOffData: any) => {
          console.log('=== updateShopDayOffInCache ===');
          console.log('Day off data to cache:', dayOffData);

          set((state) => {
            console.log('Current shopDayOffs in store:', state.shopDayOffs);

            const existingIndex = state.shopDayOffs.findIndex(
              (dayOff) => dayOff.id === dayOffData.id
            );

            if (existingIndex >= 0) {
              console.log('Updating existing day off at index:', existingIndex);
              // Update existing day off
              const updatedDayOffs = [...state.shopDayOffs];
              updatedDayOffs[existingIndex] = dayOffData;
              console.log('Updated shopDayOffs:', updatedDayOffs);
              return { shopDayOffs: updatedDayOffs };
            } else {
              console.log('Adding new day off to cache');
              const newDayOffs = [...state.shopDayOffs, dayOffData];
              console.log('New shopDayOffs:', newDayOffs);
              // Add new day off
              return { shopDayOffs: newDayOffs };
            }
          });
        },

        setShop: (shop) => {
          set({ shop });
        },

        onChangeShop: (shop) => {

          set({ shop });
        },

        autoSelectShop: (shopStaffList) => {
          const state = getState();
          const currentShop = state.shop;

          // Try to keep the same shop if it exists in the new list
          const selectedShop = shopStaffList.find(
            (s: any) => s?.shop?.id === currentShop?.id
          );

          if (selectedShop) {
            set({ shop: (selectedShop as any).shop });
          } else {
            // Otherwise select the first shop
            const firstShop = get(shopStaffList, '0.shop');
            if (firstShop) {
              set({ shop: firstShop });
            }
          }
        },

        setSSR: (isSSR) => {
          set({ isSSR });
        },

        clearError: () => {
          set({ error: null });
        },
      }),
      {
        name: 'shop-storage',
        partialize: (state) => ({
          shop: state.shop,
          shopStaff: state.shopStaff,
        }),
      }
    ),
    { name: 'shop-store' }
  )
);